import axios from "axios";
import { useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import { Typography, Box, Button, Alert } from "@mui/material";
import { Document, Page, pdfjs } from "react-pdf";
import ReactPlayer from "react-player";
import useGlobalContext from "../../hooks/useGlobalContext";
import ErrorMessage from "../../components/common/ErrorMessage";
import localize from "../../common/localize";
import LoadingPanel from "../../components/common/LoadingPanel";
import "react-pdf/dist/esm/Page/TextLayer.css";
import "react-pdf/dist/esm/Page/AnnotationLayer.css";
import PagePagination from "../../components/PagePagination";
import { getFileExtension } from "../../common/utils";
import ActionButtons from "../../components/common/ActionButtons";
import { Download } from "@mui/icons-material";

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.js`;

export default function EvidenceImageViewer({
  paramId,
  paramEvidenceId,
  paramType,
  dontUseLoadingFullScreen,
  evidencesData,
  galleryMode,
  evidenceNotes,
}) {
  const [eventId, setEventId] = useState();
  const [evidenceIdValue, setEvidenceIdValue] = useState();
  const [typeValue, setTypeValue] = useState();
  const { EVENTS_API, config } = useGlobalContext();
  const { id, evidenceId, type } = useParams();
  const [showEvidenceViewer, setShowEvidenceViewer] = useState(false);
  const [evidenceUrl, setEvidenceUrl] = useState(undefined);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [evidenceData, setEvidenceData] = useState(evidencesData);
  const [selectedEvidence, setSelectedEvidence] = useState();

  const [numOfPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageIndex, setPageIndex] = useState(0);
  const [evidenceBlob, setEvidenceBlob] = useState();

  useEffect(() => {
    if (paramId > 0) setEventId(paramId);
  }, [paramId]);

  useEffect(() => {
    if (paramEvidenceId > 0) setEvidenceIdValue(paramEvidenceId);
  }, [paramEvidenceId]);

  useEffect(() => {
    if (paramType) setTypeValue(paramType);
  }, [paramType]);

  useEffect(() => {
    if (id) setEventId(id);
  }, [id]);

  useEffect(() => {
    if (evidenceId) setEvidenceIdValue(evidenceId);
  }, [evidenceId]);

  useEffect(() => {
    if (type) setTypeValue(type);
  }, [type]);

  const getSelectedEvidence = () => {
    const index = evidenceData.Data.findIndex(
      (x) =>
        x.Id === Number.parseInt(evidenceIdValue) || x.Id === evidenceIdValue,
    );
    if (index >= 0) {
      setSelectedEvidence({ ...evidenceData.Data[index] });
    }
  };

  const getEvidences = async () => {
    try {
      const data = await axios.get(`${EVENTS_API}/${eventId}/evidence`, config);
      setEvidenceData(data.data);
      getEvidenceBlob();
    } catch (error) {
      console.log("Error loading evidence >>> ", error);
    }
  };

  useEffect(() => {
    if (evidenceUrl) {
      getSelectedEvidence();
    }
  }, [evidenceUrl]);

  useEffect(() => {
    if (evidenceUrl && selectedEvidence) {
      setShowEvidenceViewer(true);
    }
  }, [selectedEvidence]);

  useEffect(() => {
    if (eventId > 0 && evidenceIdValue > 0) {
      setShowEvidenceViewer(false);

      if (!evidencesData) {
        getEvidences();
        return;
      }
      getEvidenceBlob();
    }
  }, [evidenceIdValue, eventId]);

  useEffect(() => {
    return () => {
      if (evidenceUrl) URL.revokeObjectURL(evidenceUrl);
    };
  }, [evidenceUrl]);

  useEffect(() => {
    if (pageIndex) {
      setPageNumber(pageIndex + 1);
    }
  }, [pageIndex]);

  useEffect(() => {
    const handler = (event) => event.preventDefault();
    document.addEventListener("contextmenu", handler);
    return () => document.removeEventListener("contextmenu", handler);
  }, []);

  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
    setPageIndex(0);
    setPageNumber(1);
  };

  const onClickToDownloadEvidence = () => {
    // Create a link element
    const link = document.createElement("a");

    // Set link's href to point to the Blob URL
    link.href = evidenceUrl;
    link.download = `${selectedEvidence.Name.replace(
      " ",
      "_",
    )}.${getFileExtension(selectedEvidence.Path)}`;

    // Append link to the body
    document.body.appendChild(link);

    // Dispatch click event on the link
    // This is necessary as link.click() does not work on the latest firefox
    link.dispatchEvent(
      new MouseEvent("click", {
        bubbles: true,
        cancelable: true,
        view: window,
      }),
    );

    // Remove link from body
    document.body.removeChild(link);
  };

  const downloadButton = () => {
    let hasNotes = false;
    let notesUI = <></>;

    if (selectedEvidence?.Id > 0 && evidenceNotes?.length > 0) {
      const notes = evidenceNotes.filter(
        (x) => x.Evidence?.Id === selectedEvidence?.Id,
      );

      hasNotes = notes?.length > 0;

      if (hasNotes) {
        notesUI = (
          <Alert severity="info" className="w-full">
            <label className="report_little_text">
              <b>{notes[0].Title}: </b>
              {notes[0].Observation}
            </label>
          </Alert>
        );
      }
    }

    return (
      <Box
        className={`flex items-center ${
          hasNotes ? "flex-row-reverse" : "justify-center"
        } gap-2 w-full mt-2`}
      >
        <ActionButtons
          hideEditButton
          hideDeleteButton
          buttons={[
            {
              name: localize.download,
              icon: <Download sx={{ width: "16px", height: "16px" }} />,
              action: () => {
                onClickToDownloadEvidence();
              },
            },
          ]}
        />

        {notesUI}
      </Box>
    );
  };

  const showEvidenceAccordingToType = () => {
    // Photo
    if (Number.parseInt(typeValue) === 1) {
      return (
        <div
          style={{
            overflowY: "auto",
            display: "flex",
            flexDirection: "column",
            paddingTop: `${!dontUseLoadingFullScreen ? "5" : "0"}px`,
          }}
          className="flex flex-col items-center"
        >
          {!dontUseLoadingFullScreen && (
            <Typography>
              <b>{selectedEvidence.Name}</b>
            </Typography>
          )}
          <div style={{ border: "1px solid black", padding: "5px" }}>
            <img
              style={
                galleryMode
                  ? { border: "1px solid", marginTop: "5px", height: "350px" }
                  : { border: "1px solid", marginTop: "5px" }
              }
              src={evidenceUrl}
              alt={localize.evidence}
            />
          </div>
          {downloadButton()}
        </div>
      );
    }

    // Audio || Video
    if (Number.parseInt(typeValue) === 2 || Number.parseInt(typeValue) === 3) {
      return (
        <div
          style={{
            backgroundColor: "white",
            paddingTop: `${!dontUseLoadingFullScreen ? "5" : "0"}px`,
            display: "flex",
            flexDirection: "column",
          }}
          className={`flex flex-col items-center`}
        >
          {!dontUseLoadingFullScreen && (
            <Typography>
              <b>{selectedEvidence.Name}</b>
            </Typography>
          )}
          <ReactPlayer
            url={evidenceUrl}
            playing
            controls
            style={{ width: "40%", height: "40%", marginTop: "5px" }}
          />
          {downloadButton()}
        </div>
      );
    }

    // Document
    if (Number.parseInt(typeValue) === 4) {
      return (
        <div
          style={{
            backgroundColor: "white",
            paddingTop: `${!dontUseLoadingFullScreen ? "5" : "0"}px`,
            display: "flex",
            flexDirection: "column",
          }}
          className={`flex flex-col items-center`}
        >
          {!dontUseLoadingFullScreen && (
            <Typography>
              <b>{selectedEvidence.Name}</b>
            </Typography>
          )}

          <div className="main">
            <Document file={evidenceBlob} onLoadSuccess={onDocumentLoadSuccess}>
              <Page
                pageNumber={pageNumber}
                renderAnnotationLayer={false}
                renderTextLayer={false}
              />
            </Document>
            <div className="fullwidth flex flex-col items-center">
              <PagePagination
                customNumberOfPages={numOfPages}
                pageIndex={pageIndex}
                setPageIndex={setPageIndex}
              />
            </div>
          </div>
          {downloadButton()}
        </div>
      );
    }
  };

  const getEvidenceBlob = async () => {
    await axios({
      ...config,
      method: "get",
      responseType: "blob", // Force to receive data in a Blob Format
      url: `${EVENTS_API}/${eventId}/evidence/${evidenceIdValue}/download`,
    })
      .then((res) => {
        const blob = new Blob([res.data], {
          type: "application/octet-stream",
        });

        const urlBlob = URL.createObjectURL(blob);
        setEvidenceBlob(Number.parseInt(typeValue) === 4 ? blob : null);
        setEvidenceUrl(urlBlob);
      })
      .catch((error) => {
        setErrorMessage(error.message);
        setShowErrorModal(true);
      });
  };

  return (
    <Box className="grow">
      {showErrorModal && (
        <ErrorMessage
          message={errorMessage}
          title={localize.error_unexpected}
          showModal={showErrorModal}
          setShowModal={setShowErrorModal}
        />
      )}
      {showEvidenceViewer ? (
        showEvidenceAccordingToType()
      ) : (
        <div style={{ paddingTop: "20px" }}>
          <LoadingPanel donotUseHscreen={dontUseLoadingFullScreen} />
        </div>
      )}
    </Box>
  );
}
